const EXTENSIONS_THAT_MUST_NOT_BE_WRAPPED_AS_TEXT = new Set([
  '.csv', '.doc', '.docx', '.feature', '.html', '.json', '.log', '.md',
  '.pdf', '.png', '.jpg', '.jpeg', '.svg', '.xml', '.xls', '.xlsx',
  '.yaml', '.yml', '.zip',
]);

export const normalizeDownloadFilename = (filename?: string, fallback = 'download'): string => {
  const raw = String(filename || '').trim() || fallback;
  const withoutQuery = raw.split(/[?#]/, 1)[0];
  const basename = withoutQuery.split(/[\\/]/).pop() || fallback;
  let normalized = Array.from(basename)
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('')
    .replace(/[<>:"|?*]/g, '_')
    .trim();

  if (!normalized) {
    normalized = fallback;
  }

  if (normalized.toLowerCase().endsWith('.txt')) {
    const withoutTxt = normalized.slice(0, -4);
    const previousExtension = withoutTxt.match(/(\.[a-z0-9]+)$/i)?.[1]?.toLowerCase();
    if (previousExtension && EXTENSIONS_THAT_MUST_NOT_BE_WRAPPED_AS_TEXT.has(previousExtension)) {
      normalized = withoutTxt;
    }
  }

  if (!/\.[a-z0-9]{1,12}$/i.test(normalized)) {
    const fallbackExtension = String(fallback).match(/(\.[a-z0-9]{1,12})$/i)?.[1];
    if (fallbackExtension) {
      normalized += fallbackExtension;
    }
  }
  return normalized;
};

export const logDownloadFilename = (filename?: string, fallback = 'workflow.log'): string => {
  const normalized = normalizeDownloadFilename(filename, fallback);
  if (normalized.toLowerCase().endsWith('.log')) {
    return `${normalized.slice(0, -4)}.txt`;
  }
  return normalized.toLowerCase().endsWith('.txt') ? normalized : `${normalized}.txt`;
};

export const filenameFromContentDisposition = (
  contentDisposition?: string,
  fallback = 'download',
): string => {
  const header = String(contentDisposition || '');
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return normalizeDownloadFilename(decodeURIComponent(encoded), fallback);
    } catch (_error) {
      return normalizeDownloadFilename(encoded, fallback);
    }
  }
  const plain = header.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  return normalizeDownloadFilename(plain?.[1] || plain?.[2], fallback);
};

export const projectArtifactDownloadFilename = (
  projectName: string | null | undefined,
  artifactType: string | null | undefined,
  sourceFilename: string,
): string => {
  const labels: Record<string, string> = {
    requirements: 'requirements',
    requirements_delta: 'requirements-delta',
    requirements_merged: 'requirements-merged',
    test_cases: 'test-cases',
    test_cases_json: 'test-cases',
    gherkin: 'test-cases',
  };
  const normalizedType = String(artifactType || '').trim().toLowerCase().replace(/-/g, '_');
  const label = labels[normalizedType];
  const source = normalizeDownloadFilename(sourceFilename, 'artifact.json');
  if (!label) {
    return source;
  }
  const projectSegment = String(projectName || 'project')
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '') || 'project';
  const extension = normalizedType === 'gherkin'
    ? '.feature'
    : source.match(/(\.[a-z0-9]{1,12})$/i)?.[1]?.toLowerCase() || '.json';
  return `${projectSegment}-${label}${extension}`;
};

export const triggerDownloadFromBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = normalizeDownloadFilename(filename);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const triggerDownloadFromUrl = (url: string, filename?: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  if (filename) {
    anchor.download = normalizeDownloadFilename(filename);
  }
  try {
    const targetOrigin = new URL(url, window.location.href).origin;
    if (targetOrigin !== window.location.origin) {
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
    }
  } catch (error) {
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
  }
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};
